import { useEffect, useMemo, useState } from 'react'
import { Terminal, Loader2, ChevronsUpDown, ChevronsDownUp } from 'lucide-react'
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  Badge,
  Input,
} from '@/components/ui'
import { testRunsAPI } from '@/lib/api'
import type { TestRunPhase } from '@/lib/api'
import { formatTime } from '@/lib/utils'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import type { TestLog, LogAgent } from '@/types'

interface ActivityLogProps {
  /** If provided, render these logs directly (live-run path populated via WebSocket). */
  logs?: TestLog[]
  /** If provided and `logs` is absent, fetch the logs for this run ID via API (past-run path). */
  runId?: string
  /** Drives the "Processing..." spinner at the bottom while a run is in flight. */
  isRunning?: boolean
  /**
   * The run's current phase. Drives the live "Now doing" banner — the
   * banner names the agent that owns this phase. Absent for historical
   * runs and any caller without a live phase; the banner is then hidden.
   */
  phase?: TestRunPhase
  /** Start collapsed/expanded. Defaults to expanded. */
  defaultExpanded?: boolean
}

/**
 * Display-facing agent identity. Distinct from the persisted/runtime
 * `LogAgent` because the hint-extraction agent is emitted by the
 * runtime as `extractor` but MUST display as "Analyzer" in this UI
 * (the same `extractor → analyzer` mapping the admin appSettings UI
 * applies via `ADMIN_TO_RUNTIME`). The chip must never leak `extractor`.
 * `auditor` and `rag` (#697) pass through unchanged — their runtime
 * names already read well as display labels.
 */
type DisplayAgent =
  | 'planner'
  | 'generator'
  | 'executor'
  | 'healer'
  | 'analyzer'
  | 'auditor'
  | 'rag'
  | 'system'

/** Map a persisted/runtime agent tag to its display identity. The
 * `extractor → analyzer` rename is the only cross-name mapping;
 * `auditor` / `rag` pass through unchanged. An absent tag (rows
 * written before #697 added agent attribution) falls back to
 * `system`. */
function toDisplayAgent(agent: LogAgent | undefined): DisplayAgent {
  if (agent === 'extractor') return 'analyzer'
  if (
    agent === 'planner' ||
    agent === 'generator' ||
    agent === 'executor' ||
    agent === 'healer' ||
    agent === 'auditor' ||
    agent === 'rag'
  ) {
    return agent
  }
  return 'system'
}

/**
 * Per-agent accent palette — mirrors `docs/mockups/activity-log-palette.html`.
 *
 * `c` / `bg` / `bd` reference the `--agent-*` CSS custom properties
 * defined in `index.css` under `:root` (light) and `.dark` (dark), so
 * each agent's accent flips with the active theme rather than being a
 * hardcoded dark-only hex (#697). The component keeps its inline-`style`
 * structure — only the values became theme-flipping vars.
 */
const AGENT_STYLE: Record<DisplayAgent, { label: string; c: string; bg: string; bd: string }> = {
  planner: { label: 'Planner', c: 'var(--agent-planner)', bg: 'var(--agent-planner-bg)', bd: 'var(--agent-planner-bd)' },
  generator: { label: 'Generator', c: 'var(--agent-generator)', bg: 'var(--agent-generator-bg)', bd: 'var(--agent-generator-bd)' },
  executor: { label: 'Executor', c: 'var(--agent-executor)', bg: 'var(--agent-executor-bg)', bd: 'var(--agent-executor-bd)' },
  healer: { label: 'Healer', c: 'var(--agent-healer)', bg: 'var(--agent-healer-bg)', bd: 'var(--agent-healer-bd)' },
  analyzer: { label: 'Analyzer', c: 'var(--agent-analyzer)', bg: 'var(--agent-analyzer-bg)', bd: 'var(--agent-analyzer-bd)' },
  auditor: { label: 'Auditor', c: 'var(--agent-auditor)', bg: 'var(--agent-auditor-bg)', bd: 'var(--agent-auditor-bd)' },
  // The runtime/persisted tag stays `rag` (Retrieval-Augmented
  // Generation) — what the codebase calls hint retrieval. The chip
  // label here is `Agent Memory` to match the brand vocabulary
  // everywhere users see it (sidebar nav, Analytics tab, Hints page).
  rag: { label: 'Agent Memory', c: 'var(--agent-rag)', bg: 'var(--agent-rag-bg)', bd: 'var(--agent-rag-bd)' },
  system: { label: 'System', c: 'var(--agent-system)', bg: 'var(--agent-system-bg)', bd: 'var(--agent-system-bd)' },
}

/**
 * Fixed pill order — so a pill that appears mid-run lands in a stable
 * slot. Ordered by invocation sequence through the run: the planner
 * runs first; `rag` (hint retrieval) feeds the generator's prompt, so
 * it precedes it; the generator runs; the `auditor` grades the
 * generator's output, so it follows it; then executor → healer;
 * `analyzer` (hint extraction) closes the run. `system` is
 * intentionally absent: System entries are neutral infrastructure
 * logs, not a pipeline component's work. They still get a System chip
 * on their rows, but no pill — and so no per-component filter toggle.
 */
const PILL_AGENT_ORDER: DisplayAgent[] = [
  'planner',
  'rag',
  'generator',
  'auditor',
  'executor',
  'healer',
  'analyzer',
]

/**
 * Map a run phase to its display agent — drives the "Now doing"
 * banner. Mirrors the server's `phaseToAgent` (`verifying` folds into
 * the healer; `processing` is the analyzer). `preparing` / `healed` /
 * an absent phase have no specific agent → the banner stays hidden.
 */
function phaseToDisplayAgent(phase: TestRunPhase | undefined): DisplayAgent | null {
  switch (phase) {
    case 'planning':
      return 'planner'
    case 'generating':
      return 'generator'
    case 'executing':
      return 'executor'
    case 'healing':
    case 'verifying':
      return 'healer'
    case 'processing':
      return 'analyzer'
    default:
      return null
  }
}

/**
 * One-liner shown in the "Now doing" banner per active agent. Keyed by
 * every `DisplayAgent` so the record stays total. `auditor` and `rag`
 * never actually surface in the banner — they aren't phases, so
 * `phaseToDisplayAgent` never returns them — but their entries keep
 * the `Record<DisplayAgent, string>` type total (#697).
 */
const NOW_DOING: Record<DisplayAgent, string> = {
  planner: 'exploring the application and planning test cases…',
  generator: 'writing Playwright test specs…',
  executor: 'running the generated tests…',
  healer: 'diagnosing failures and re-running to verify fixes…',
  analyzer: 'extracting hints from the run…',
  auditor: 'grading the quality of the agents’ output…',
  rag: 'retrieving relevant hints from memory…',
  system: 'working…',
}

/** Parsed shape of a `TestLog.details` thinking payload. */
interface Thinking {
  reasoning: string | null
  actions: string[]
}

/**
 * Parse the AgentRunner's `details` payload into Reasoning + Actions.
 * The runner emits a plain text block:
 *
 *   Reasoning:
 *   <narration>
 *
 *   Actions:
 *   → tool_a(...)
 *   → tool_b(...)
 *
 * Either section may be absent. Anything that doesn't match the
 * headers is treated as raw reasoning so no thinking payload is lost.
 */
function parseThinking(details: string): Thinking {
  const reasonMatch = details.match(/Reasoning:\n([\s\S]*?)(?:\n\nActions:\n|$)/)
  const actionsMatch = details.match(/Actions:\n([\s\S]*)$/)
  const actions = actionsMatch
    ? actionsMatch[1]
        .split('\n')
        .map((l) => l.replace(/^→\s*/, '').trim())
        .filter(Boolean)
    : []
  let reasoning = reasonMatch ? reasonMatch[1].trim() : null
  if (!reasoning && actions.length === 0) {
    // No recognized headers — surface the whole payload as reasoning.
    reasoning = details.trim() || null
  }
  return { reasoning, actions }
}

/** A single log row + its expand-on-demand thinking panel. The parent
 * fully owns `expanded` so it can:
 *   - default new rows to expanded while the run is in progress (#1205)
 *   - persist explicit operator collapses across live updates
 *   - apply bulk expand-all / collapse-all without an indirection signal
 */
function LogEntry({
  log,
  expanded,
  onToggle,
}: {
  log: TestLog
  expanded: boolean
  onToggle: () => void
}) {
  const tz = useUserTimezone()
  const agent = toDisplayAgent(log.agent)
  const style = AGENT_STYLE[agent]
  const hasThinking = Boolean(log.details && log.details.trim())
  const thinking = hasThinking ? parseThinking(log.details as string) : null

  // Log-message color → theme tokens so the text reads in both light
  // and dark themes (#697). The old hardcoded `#d3d8de` for info was
  // the light-theme bug — a light gray invisible on a white surface.
  // info → the theme foreground; error → the destructive token;
  // warn → an amber that holds up in both themes.
  const levelClass =
    log.level === 'error'
      ? 'text-destructive'
      : log.level === 'warn'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-foreground'

  return (
    <div>
      <div
        className="flex items-baseline gap-2 py-1 pl-2 pr-2 rounded-sm hover:bg-muted"
        style={{ borderLeft: `3px solid ${style.c}` }}
      >
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
          {formatTime(log.timestamp, tz)}
        </span>
        <span
          className="shrink-0 rounded px-1.5 font-mono text-[10px] font-bold uppercase tracking-wide"
          style={{ color: style.c, backgroundColor: style.bg, border: `1px solid ${style.bd}` }}
        >
          {style.label}
        </span>
        <span className={`flex-1 break-words ${levelClass}`}>
          {log.message}
        </span>
        {hasThinking && (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 rounded px-1.5 py-px text-[10px] font-medium"
            style={{ color: style.c, backgroundColor: style.bg, border: `1px solid ${style.bd}` }}
            aria-expanded={expanded}
          >
            {expanded ? '▾ thinking' : '▸ thinking'}
          </button>
        )}
      </div>
      {expanded && thinking && (
        // Thinking panel — structural colors are theme tokens (#697)
        // so the Reasoning prose + Actions list read in both themes.
        // The per-agent accent (`style.c`) is kept for the agent-
        // colored border + section headers + arrows — that flips via
        // the `--agent-*` CSS vars.
        <div
          className="ml-8 mr-2 mb-2 mt-1 overflow-hidden rounded-md bg-muted"
          style={{ border: `1px solid ${style.bd}` }}
        >
          {thinking.reasoning && (
            <div className="border-b border-border px-3 py-2">
              <div
                className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                style={{ color: style.c }}
              >
                Reasoning
              </div>
              <div className="whitespace-pre-wrap text-[12px] italic text-foreground">
                {thinking.reasoning}
              </div>
            </div>
          )}
          {thinking.actions.length > 0 && (
            <div className="px-3 py-2">
              <div
                className="mb-1 text-[10px] font-bold uppercase tracking-wide"
                style={{ color: style.c }}
              >
                Actions · {thinking.actions.length} tool {thinking.actions.length === 1 ? 'call' : 'calls'}
              </div>
              <div className="flex flex-col gap-1">
                {thinking.actions.map((act, i) => (
                  <div key={i} className="flex gap-2 font-mono text-[11px] text-muted-foreground">
                    <span style={{ color: style.c }}>→</span>
                    <span className="text-foreground">{act}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Activity Log panel shared between the live Test Runner view and the
 * historical Results view.
 *
 * - Live case: caller passes `logs` (usually `currentRun.logs`), which the
 *   WebSocket progress stream keeps up to date.
 * - Past-run case: caller passes only `runId`, and the component fetches
 *   the persisted logs from `/api/test-runs/:id/logs`.
 *
 * Persisted logs land durably per entry in the `TestRunLog` Mongo
 * collection (written during the run by TestRunLogRepository.append),
 * carrying the `agent` tag + `details` thinking payload — so a page
 * refresh or a later visit renders the agent-attributed log identically
 * for live and historical runs.
 *
 * Redesigned for #697: agent-attributed, color-coded entries; agent
 * pills that appear as each agent first acts and toggle a per-agent
 * filter; a live "Now doing" banner; and expand-on-demand "thinking".
 */
export function ActivityLog({ logs, runId, isRunning, phase, defaultExpanded = true }: ActivityLogProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [filter, setFilter] = useState('')
  /** Agents currently selected in the pill filter. Empty set = no
   * agent filter (show all) — the natural "nothing toggled" state. */
  const [agentFilter, setAgentFilter] = useState<Set<DisplayAgent>>(new Set())
  const [fetched, setFetched] = useState<TestLog[] | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  /**
   * Per-row thinking-panel expansion, keyed by the row's sourceIndex
   * (the row's index in the unfiltered log stream — stable across text
   * + agent filter changes and across appends, since the log stream is
   * append-only). The Map is the source of truth for every visible
   * row; the parent owns it so that:
   *
   *   - on mount + on each new row that streams in, the default is
   *     `isRunning` — so an operator opening a live run sees every
   *     section expanded by default, including sections that appear
   *     after mount (#1205);
   *   - explicit per-row toggles and bulk expand-all / collapse-all
   *     commands both write directly here, so operator intent persists
   *     across subsequent live updates — a row the operator collapsed
   *     does NOT re-expand when fresh logs arrive;
   *   - flipping `isRunning` mid-view (the run completes while the
   *     operator is watching) does NOT mutate already-seeded entries,
   *     so existing expanded sections stay expanded.
   */
  const [expandedRows, setExpandedRows] = useState<Map<number, boolean>>(new Map())

  // Fetch only when caller doesn't have the logs in hand. Live-run callers
  // always pass `logs`; past-run callers pass only `runId`.
  useEffect(() => {
    // Clear any stale error up front — covers switching to the `logs`
    // prop, a `runId` change, and the start of a fresh fetch. A
    // successful fetch then simply leaves it cleared.
    setFetchError(null)
    if (logs || !runId) {
      setFetched(null)
      return
    }
    let cancelled = false
    testRunsAPI.getLogs(runId)
      .then((resp) => {
        if (cancelled) return
        setFetched(resp.logs.map((l) => ({
          timestamp: new Date(l.timestamp),
          level: l.level,
          message: l.message,
          details: l.details,
          agent: l.agent,
        })))
      })
      .catch((err) => {
        if (!cancelled) setFetchError(err instanceof Error ? err.message : String(err))
      })
    return () => { cancelled = true }
  }, [logs, runId])

  const displayLogs: TestLog[] = logs ?? fetched ?? []

  // Distinct agents that have acted, in fixed order — drives the pill
  // row, so a pill "appears" the first time its agent emits a line.
  const presentAgents = useMemo(() => {
    const seen = new Set<DisplayAgent>()
    for (const log of displayLogs) seen.add(toDisplayAgent(log.agent))
    return PILL_AGENT_ORDER.filter((a) => seen.has(a))
  }, [displayLogs])

  // "Now doing" — the agent owning the live phase. Hidden once the run
  // is no longer running, or when the phase has no specific agent.
  const nowAgent = isRunning ? phaseToDisplayAgent(phase) : null

  // Carry each row's index in the *unfiltered* source so `LogEntry`
  // keys stay stable when the text/agent filters change. Keying by the
  // filtered index would reindex on every filter change and a row's
  // expanded "thinking" state would jump to a different entry.
  const filteredLogs = displayLogs
    .map((log, sourceIndex) => ({ log, sourceIndex }))
    .filter(({ log }) => {
      if (filter && !log.message.toLowerCase().includes(filter.toLowerCase())) return false
      if (agentFilter.size > 0 && !agentFilter.has(toDisplayAgent(log.agent))) return false
      return true
    })

  // Whether any *currently visible* entry carries a thinking payload —
  // gates the header's expand-all control. Derived from the filtered
  // set so the control disappears when filters leave nothing to expand.
  const hasAnyThinking = filteredLogs.some(
    ({ log }) => Boolean(log.details && log.details.trim()),
  )

  /**
   * Seed `expandedRows` for any thinking-bearing row not yet seen.
   * Default = `isRunning` at the moment the row appears, so:
   *   - mounting against a live run seeds every row to `true` → expanded
   *   - new rows arriving mid-run while still running also seed to `true`
   *   - mounting against a completed run seeds every row to `false`
   * Existing entries are NEVER overwritten — operator collapses persist
   * across live updates, and a mid-view run completion doesn't force
   * already-expanded rows back to collapsed.
   *
   * A single functional `setExpandedRows` call handles all new rows in
   * one pass; the early return when nothing new appeared avoids a
   * pointless re-render on every websocket tick that didn't add a
   * thinking-bearing entry.
   */
  useEffect(() => {
    setExpandedRows((prev) => {
      let next: Map<number, boolean> | null = null
      const defaultExpansion = Boolean(isRunning)
      displayLogs.forEach((log, sourceIndex) => {
        if (!log.details || !log.details.trim()) return
        if (prev.has(sourceIndex)) return
        if (next === null) next = new Map(prev)
        next.set(sourceIndex, defaultExpansion)
      })
      return next ?? prev
    })
    // `expandedRows` is intentionally NOT in the dep array: the effect's
    // job is to react to changes in the log stream, not to its own
    // writes. Including it would cause a render-update loop on every
    // seed. The `prev.has(...)` guard inside the updater defends against
    // double-seeding the same row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLogs, isRunning])

  /**
   * Toggle a single row's expanded state — operator's explicit click.
   * Persists across subsequent live updates because the seeding effect
   * never overwrites an existing entry.
   */
  function toggleRow(sourceIndex: number) {
    setExpandedRows((prev) => {
      const next = new Map(prev)
      next.set(sourceIndex, !next.get(sourceIndex))
      return next
    })
  }

  /**
   * Bulk expand-all / collapse-all — write the same value into every
   * currently-visible thinking-bearing row. Hidden rows (filtered out)
   * keep their current state so toggling filters back doesn't surface
   * an unexpected mass change the operator never asked for.
   */
  const isAllExpanded = filteredLogs
    .filter(({ log }) => Boolean(log.details && log.details.trim()))
    .every(({ sourceIndex }) => expandedRows.get(sourceIndex) === true)
  function setAllVisible(expandedValue: boolean) {
    setExpandedRows((prev) => {
      const next = new Map(prev)
      for (const { log, sourceIndex } of filteredLogs) {
        if (!log.details || !log.details.trim()) continue
        next.set(sourceIndex, expandedValue)
      }
      return next
    })
  }

  function toggleAgent(agent: DisplayAgent) {
    setAgentFilter((prev) => {
      const next = new Set(prev)
      if (next.has(agent)) next.delete(agent)
      else next.add(agent)
      return next
    })
  }

  return (
    <Card>
      <CardHeader
        className="flex flex-row items-center justify-between pb-2 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <CardTitle className="flex items-center gap-2">
          <Terminal className="h-4 w-4" />
          Run Log
          <Badge variant="outline" className="ml-1 text-xs">{displayLogs.length}</Badge>
        </CardTitle>
        {/* Expand/collapse every thinking panel at once. Shown only
            when the panel is open and at least one entry has thinking.
            stopPropagation so it doesn't also toggle the whole panel.
            No separate panel-toggle chevron — clicking the header
            itself already expands/collapses the Run Log window. */}
        {expanded && hasAnyThinking && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setAllVisible(!isAllExpanded)
            }}
            className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {isAllExpanded ? (
              <><ChevronsDownUp className="h-3 w-3" />Collapse all</>
            ) : (
              <><ChevronsUpDown className="h-3 w-3" />Expand all</>
            )}
          </button>
        )}
      </CardHeader>
      {expanded && (
        <CardContent>
          {/* Agent pills — appear as each one first acts; click to filter.
              The label reads "Agents" for the regular user: the chip
              taxonomy (planner / generator / executor / healer / analyzer
              / auditor / memory) names what the system is doing without
              leaking internal terms like "component" or "RAG". */}
          {presentAgents.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Agents
              </span>
              {presentAgents.map((agent) => {
                const style = AGENT_STYLE[agent]
                const selected = agentFilter.size === 0 || agentFilter.has(agent)
                const isActive = nowAgent === agent
                return (
                  <button
                    key={agent}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleAgent(agent) }}
                    aria-pressed={agentFilter.has(agent)}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-[filter] hover:brightness-125"
                    style={{
                      color: style.c,
                      backgroundColor: style.bg,
                      border: `1px solid ${style.bd}`,
                      opacity: selected ? 1 : 0.4,
                    }}
                  >
                    <span
                      className="h-[7px] w-[7px] rounded-full"
                      style={{ backgroundColor: style.c }}
                    />
                    {style.label}
                    <span className={isActive ? 'inline-block animate-spin' : ''}>
                      {isActive ? '⟳' : '✓'}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* "Now doing" banner — the currently-active agent. */}
          {nowAgent && (
            <div
              className="mb-2 flex items-center gap-2.5 rounded-md px-3 py-2"
              style={{
                backgroundColor: AGENT_STYLE[nowAgent].bg,
                border: `1px solid ${AGENT_STYLE[nowAgent].bd}`,
              }}
            >
              <span
                className="inline-block animate-spin text-sm"
                style={{ color: AGENT_STYLE[nowAgent].c }}
              >
                ⟳
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                Now
              </span>
              <span
                className="text-xs font-bold"
                style={{ color: AGENT_STYLE[nowAgent].c }}
              >
                {AGENT_STYLE[nowAgent].label}
              </span>
              <span className="text-xs text-foreground">
                {NOW_DOING[nowAgent]}
              </span>
            </div>
          )}

          {/* Text filter (kept) — combines with the agent pill filter. */}
          <div className="mb-2">
            <Input
              placeholder="Filter logs..."
              value={filter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
              className="h-7 text-xs"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              clearable
            />
          </div>

          <div className="space-y-1 text-xs max-h-80 overflow-y-auto bg-muted/50 rounded p-3">
            {fetchError && (
              <div className="text-destructive">Failed to load logs: {fetchError}</div>
            )}
            {filteredLogs.map(({ log, sourceIndex }) => (
              <LogEntry
                key={sourceIndex}
                log={log}
                expanded={expandedRows.get(sourceIndex) === true}
                onToggle={() => toggleRow(sourceIndex)}
              />
            ))}
            {isRunning && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Processing...</span>
              </div>
            )}
            {!isRunning && displayLogs.length === 0 && !fetchError && (
              <div className="text-muted-foreground">No log entries.</div>
            )}
            {!isRunning && displayLogs.length > 0 && filteredLogs.length === 0 && !fetchError && (
              <div className="text-muted-foreground">No log entries match the current filter.</div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}
